/**
 * Game channel preference module.
 *
 * Manages a sticky, per-game-id release channel preference (e.g. tictactoe ->
 * "preview"). This is the fallback channel startGame uses when a caller
 * doesn't explicitly pass one — see gameInRoom.ts's startGame — so it's what
 * makes a room "keep following preview" across rematches/restarts (openGame,
 * restartGame) rather than reverting to "stable" every time a new game starts.
 */

import type { RoomStorage, BroadcastChannel } from '../../types/infrastructure.types'
import type { GetGameChannelPreferenceArgs, SetGameChannelPreferenceArgs } from '../../schemas/action.schemas'

export interface GameChannelPreferenceContainer {
  storage: RoomStorage
  broadcastChannel: BroadcastChannel
}

/**
 * Creates game channel preference management functions for a room.
 */
export function makeGameChannelPreference(container: GameChannelPreferenceContainer) {
  const { storage, broadcastChannel } = container

  /**
   * Get the sticky channel preference for a game, or null if none is set
   * (meaning startGame falls back to DEFAULT_GAME_CHANNEL).
   */
  const getGameChannelPreference = async (args: GetGameChannelPreferenceArgs): Promise<{ channel: string | null }> => {
    const { gameId } = args
    const state = storage.getState()
    return { channel: state.gameChannelPreferenceById?.[gameId] ?? null }
  }

  /**
   * Set the sticky channel preference for a game. Broadcasts
   * 'gameChannelPreferenceChanged' so every connected client's debug UI
   * stays in sync, independent of whether a game is currently active.
   */
  const setGameChannelPreference = async (
    args: SetGameChannelPreferenceArgs
  ): Promise<{ success: boolean }> => {
    const { gameId, channel } = args
    const state = storage.getState()

    const newState = {
      ...state,
      gameChannelPreferenceById: {
        ...state.gameChannelPreferenceById,
        [gameId]: channel,
      },
    }
    storage.setState(newState)

    broadcastChannel.broadcast({
      type: 'gameChannelPreferenceChanged',
      data: { gameId, channel },
    })

    return { success: true }
  }

  return {
    getGameChannelPreference,
    setGameChannelPreference,
  }
}

export type GameChannelPreferenceModule = ReturnType<typeof makeGameChannelPreference>
